import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';

import { DateAdapter } from '../../client/helpers/serializer/adapters/date.adapter';
import { DateOrderAdapter } from '../../client/helpers/serializer/adapters/date-order.adapter';
import { Id } from './id';
import { IdAdapter } from '../../client/helpers/serializer/adapters/id.adapter';
import { IdInterface, OrderAction, OrderInterface, OrderType, TypeOfWithdraw } from '../../../core';
import { OrderClient } from './order-client';
import { OrderTypeAdapter } from '../../client/helpers/serializer/adapters/order-type.adapter';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { TypeOfWithdrawAdapter } from '../../client/helpers/serializer/adapters/type-of-withdraw.adapter';

@JsonObject('Order')
export class Order implements OrderInterface {
    @JsonProperty('OrderId', IdAdapter, true)
    private _id: Id = undefined;

    @JsonProperty('Type', OrderTypeAdapter, true)
    private _type: OrderType = undefined;

    private _action: OrderAction = undefined;

    @JsonProperty('FundAmmount', Number, true)
    private _amount: number = undefined;

    @JsonProperty('FundShares', Number, true)
    private _shares: number = undefined;

    @JsonProperty('OrderWithShares', Boolean, true)
    private _withShares: boolean = undefined;

    @JsonProperty('IsTotal', Boolean, true)
    private _isTotal: boolean = undefined;

    @JsonProperty('ReferenceAmmount', Number, true)
    private _referenceAmount: number = undefined;

    @JsonProperty('ExternalTransfer_FundManager', StringAdapter, true)
    private _externalTransferManager: string = undefined;

    @JsonProperty('ExternalTransfer_FundName', StringAdapter, true)
    private _externalTransferName: string = undefined;

    @JsonProperty('ExternalTransfer_ISIN', StringAdapter, true)
    private _externalTransferISIN: string = undefined;

    @JsonProperty('ExternalTransfer_Contract', StringAdapter, true)
    private _externalTransferContract: string = undefined;

    @JsonProperty('ExternalTransfer_Trader', StringAdapter, true)
    private _externalTransferTrader: string = undefined;

    @JsonProperty('InternalTransfer_FundCode', StringAdapter, true)
    private _internalTransferCode: string = undefined;

    @JsonProperty('InternalTransfer_FundShares', Number, true)
    private _internalTransferShares: number = undefined;

    @JsonProperty('InternalTransfer_FundAmmount', Number, true)
    private _internalTransferAmount: number = undefined;

    @JsonProperty('TypeOfWithdraw', TypeOfWithdrawAdapter, true)
    private _typeOfWithdraw: TypeOfWithdraw = undefined;

    @JsonProperty('SendFundProspectus', Boolean, true)
    private _sendFundProspectus: boolean = undefined;

    @JsonProperty('SendQuarterlyReport', Boolean, true)
    private _sendQuarterlyReport: boolean = undefined;

    @JsonProperty('SendAnnualReport', Boolean, true)
    private _sendAnnualReport: boolean = undefined;

    @JsonProperty('DisclaimerChecked', Boolean, true)
    private _disclaimer: boolean = undefined;

    @JsonProperty('OrderClients', [OrderClient], true)
    private _clients: OrderClient[] = [];

    @JsonProperty('OrderDate', DateOrderAdapter, true)
    private _date: Moment = undefined;

    @JsonProperty('IsCancelOrder', Boolean, true)
    private _canceled: boolean = undefined;

    @JsonProperty('PeriodicNumberOfMonths', Number, true)
    private _periodNumberOfMonths: number = undefined;

    @JsonProperty('PeriodicOrderStartDate', DateAdapter, true)
    private _periodOrderStartDate: Moment = undefined;

    @JsonProperty('DestinationContractCode', IdAdapter, true)
    private _destinationContractCode: IdInterface = undefined;

    @JsonProperty('HasCampaign', Boolean, true)
    private _hasCampaign = false;


    constructor() {
        this._type = OrderType.SUBSCRIPTION;
        this._referenceAmount = 0;
        this._withShares = false;
        this._isTotal = false;
        this._sendFundProspectus = false;
        this._sendQuarterlyReport = false;
        this._sendAnnualReport = false;
        this._disclaimer = false;
        this._canceled = false;
        //this._periodNumberOfMonths = 0;
    }

    get id(): Id {
        return this._id;
    }

    get type(): OrderType {
        return this._type;
    }

    set type(value: OrderType) {
        this._type = value;
    }

    get action(): OrderAction {
        if (this._action === undefined) {
            if (this.isTotal) {
                this._action = OrderAction.TOTAL;
            } else if (this.withShares) {
                this._action = OrderAction.SHARES;
            } else {
                this._action = OrderAction.AMOUNT;
            }
        }

        return this._action;
    }

    set action(value: OrderAction) {
        this._action = value;

        switch (this._action) {
            case OrderAction.TOTAL:
                this.amount = 0;
                this.shares = 0;
                this.isTotal = true;
                break;
            case OrderAction.SHARES:
                this.referenceAmount = 0;
                this.isTotal = false;
                break;
            case OrderAction.AMOUNT:
                this.referenceAmount = 0;
                this.isTotal = false;
                break;
        }
    }

    get amount(): number {
        return this._amount;
    }

    set amount(value: number) {
        this._amount = value;
    }

    get shares(): number {
        return this._shares;
    }

    set shares(value: number) {
        this._shares = value;
    }

    get withShares(): boolean {
        return this._withShares;
    }

    set withShares(value: boolean) {
        this._withShares = value;
    }

    get isTotal(): boolean {
        return this._isTotal;
    }

    set isTotal(value: boolean) {
        this._isTotal = value;
    }

    get referenceAmount(): number {
        return this._referenceAmount;
    }

    set referenceAmount(value: number) {
        this._referenceAmount = value;
    }

    get externalTransferManager(): string {
        return this._externalTransferManager;
    }

    set externalTransferManager(value: string) {
        this._externalTransferManager = value;
    }

    get externalTransferName(): string {
        return this._externalTransferName;
    }

    set externalTransferName(value: string) {
        this._externalTransferName = value;
    }

    get externalTransferISIN(): string {
        return this._externalTransferISIN;
    }

    set externalTransferISIN(value: string) {
        this._externalTransferISIN = value ? value.toUpperCase() : value;
    }

    get externalTransferContract(): string {
        return this._externalTransferContract;
    }

    set externalTransferContract(value: string) {
        this._externalTransferContract = value;
    }

    get externalTransferTrader(): string {
        return this._externalTransferTrader;
    }

    set externalTransferTrader(value: string) {
        this._externalTransferTrader = value;
    }

    get internalTransferCode(): string {
        return this._internalTransferCode;
    }

    set internalTransferCode(value: string) {
        this._internalTransferCode = value;
    }

    get internalTransferShares(): number {
        return this._internalTransferShares;
    }

    set internalTransferShares(value: number) {
        this._internalTransferShares = value;
    }

    get internalTransferAmount(): number {
        return this._internalTransferAmount;
    }

    set internalTransferAmount(value: number) {
        this._internalTransferAmount = value;
    }

    get typeOfWithdraw(): TypeOfWithdraw {
        return this._typeOfWithdraw;
    }

    set typeOfWithdraw(value: TypeOfWithdraw) {
        this._typeOfWithdraw = value;
    }

    get sendFundProspectus(): boolean {
        return this._sendFundProspectus;
    }

    get sendQuarterlyReport(): boolean {
        return this._sendQuarterlyReport;
    }

    get sendAnnualReport(): boolean {
        return this._sendAnnualReport;
    }

    get disclaimer(): boolean {
        return this._disclaimer;
    }

    set disclaimer(value: boolean) {
        this._disclaimer = value;
    }

    get clients(): OrderClient[] {
        if (!this._clients) {
            this._clients = [];
        }

        return this._clients;
    }

    set clients(value: OrderClient[]) {
        this._clients = value;
    }

    get date(): Moment {
        return this._date;
    }

    set date(value: Moment) {
        this._date = value;
    }

    get canceled(): boolean {
        return this._canceled;
    }

    set canceled(value: boolean) {
        this._canceled = value;
    }

    get periodNumberOfMonths(): number {
        if(this._periodNumberOfMonths == 0){
            return this._periodNumberOfMonths = null;
        }else{
            return this._periodNumberOfMonths;

        }
    }

    set periodNumberOfMonths(value: number) {
        this._periodNumberOfMonths = value;
    }

    get periodOrderStartDate(): Moment {
        return this._periodOrderStartDate;
    }

    set periodOrderStartDate(value: Moment) {
        this._periodOrderStartDate = value;
    }

    get destinationContractCode(): IdInterface {
        return this._destinationContractCode;
    }

    set destinationContractCode(value: IdInterface) {
        this._destinationContractCode = value;
    }

    get hasCampaign(): boolean {
        return this._hasCampaign;
    }

    set hasCampaign(value: boolean) {
        this._hasCampaign = value;
    }
}
